import React, { useMemo } from "react";

import { css, cx } from "@emotion/css";
import Typography from "@mui/material/Typography";

import { useStyles } from "@/hooks/useStyles.ts";
import { Message, groupReactions, myReaction } from "@/models/MessagingModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Tag } from "@/uiKit/atoms/Tag.tsx";

export type MessageReactionsProps = {
  message: Message;
  meId: string;
  isPractitionerOnly: boolean;
  isSender: boolean;
  className?: string;
};

export const MessageReactions: React.FC<MessageReactionsProps> = ({
  message,
  isPractitionerOnly,
  meId,
  isSender,
  className,
}) => {
  const styles = useStyles(makeStyles);
  const toggleReaction = Queries.messaging.useToggleReaction();

  const grouped = useMemo(() => groupReactions(message.reactions, meId), [message.reactions, meId]);

  if (message.deleted || grouped.length === 0) {
    return null;
  }

  const currentEmoji = myReaction(message.reactions, meId);

  return (
    <div className={cx(styles.container, className)}>
      {grouped.map(group => (
        <Tag
          key={group.emoji}
          data-action="messaging.reaction.toggle"
          data-emoji={group.emoji}
          color="blackAndWhite"
          clickable={!isSender}
          onClick={() =>
            toggleReaction.mutate({
              patientId: message.subjectId,
              messageId: message.id,
              emoji: group.emoji,
              currentEmoji,
            })
          }
          classes={{
            root: cx({
              [styles.chip]: true,
              [styles.chipPractitioner]: isPractitionerOnly,
              [styles.chipSelected]: group.reactedByMe,
              [styles.chipSelectedPractitioner]: group.reactedByMe && isPractitionerOnly,
              [styles.disabled]: isSender,
            }),
          }}
          size="medium"
          label={
            group.count > 1 ? (
              <Typography variant="h5" className={styles.chipContent}>
                <Typography variant="h5" component="span">
                  {group.emoji}
                </Typography>
                <Typography variant="body" component="span" className={styles.chipCount}>
                  {group.count}
                </Typography>
              </Typography>
            ) : (
              <Typography variant="h5">{group.emoji}</Typography>
            )
          }
        />
      ))}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
  }),
  chip: css({
    borderRadius: theme.spacing(12),
    minWidth: theme.spacing(22),
    outline: `3px solid ${theme.palette.grey[50]}`,
    ":hover": {
      backgroundColor: theme.palette.grey[300],
    },
  }),
  chipPractitioner: css({
    outline: `3px solid ${theme.palette.green.contrastText}`,
    ":hover": {
      backgroundColor: theme.palette.green.main,
    },
  }),
  chipSelected: css({
    backgroundColor: theme.palette.grey[50],
  }),
  chipSelectedPractitioner: css({
    backgroundColor: theme.palette.green.contrastText,
  }),
  disabled: css({
    pointerEvents: "none",
  }),
  chipContent: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
  }),
  chipCount: css({
    fontWeight: "bold",
  }),
});
